import { APIClient } from './APIClient';
import { API_URL } from '../settings/local_settings';

export class FieldSensitivityService {
    // Cache estático compartido entre todas las instancias
    static cache = {};
    static cacheLoaded = false;

    /**
     * Carga las sensibilidades para múltiples fields
     * @param {Array<string>} fields - Array de field IDs
     * @returns {Promise<Object>} - Objeto con las sensibilidades por field
     */
    static async loadSensitivities(fields) {
        const url = `${API_URL}looker-cc/field-sensitivities/`;
        const response = await new APIClient(url).call('POST', { fields });
        
        if (response.status === 'OK') {
            const sensitivities = response.data.sensitivities || {};
            // Actualizar el cache estático
            this.cache = { ...this.cache, ...sensitivities };
            this.cacheLoaded = true;
            return sensitivities;
        }
        
        throw new Error(response.error_message || 'Error al cargar sensibilidades');
    }

    /**
     * Obtiene las sensibilidades del cache si están disponibles
     * @param {Array<string>} fields - Array de field IDs
     * @returns {Object} - Objeto con las sensibilidades por field del cache, o null si no están disponibles
     */
    static getCachedSensitivities(fields) {
        if (!this.cacheLoaded || !fields || fields.length === 0) {
            return null;
        }
        
        const cachedSensitivities = {};
        let allFound = true;
        
        for (const field of fields) {
            if (this.cache[field]) {
                cachedSensitivities[field] = this.cache[field];
            } else {
                allFound = false;
                break;
            }
        }
        
        return allFound && Object.keys(cachedSensitivities).length > 0 ? cachedSensitivities : null;
    }

    /**
     * Obtiene la sensibilidad de un field específico (incluye agencia y usuario)
     * @param {string} field - Field ID
     * @returns {Promise<Object>} - Objeto con sensibilidad de agencia y usuario
     */
    static async getFieldSensitivity(field) {
        const url = `${API_URL}looker-cc/field-sensitivity/${field}/`;
        const response = await new APIClient(url).call('GET');
        
        if (response.status === 'OK') {
            return response.data;
        }
        
        throw new Error(response.error_message || 'Error al obtener sensibilidad');
    }

    /**
     * Actualiza la sensibilidad de un field
     * @param {string} field - Field ID
     * @param {number|null} sensitivityValue1 - Umbral BUENO (puede ser null para nivel usuario)
     * @param {number|null} sensitivityValue2 - Umbral MUY BUENO/MUY MALO (puede ser null para nivel usuario)
     * @param {boolean} isUserLevel - true para usuario, false para agencia
     * @param {boolean} showSensitivityColor - Mostrar color en celda según sensibilidad (default: true)
     * @returns {Promise<Object>} - Respuesta de la actualización
     */
    static async updateSensitivity(field, sensitivityValue1, sensitivityValue2, isUserLevel = false, showSensitivityColor = true) {
        const url = `${API_URL}looker-cc/field-sensitivity/${field}/`;
        const requestBody = {
            sensitivity_value_1: sensitivityValue1,
            sensitivity_value_2: sensitivityValue2,
            show_sensitivity_color: showSensitivityColor,
            is_user_level: isUserLevel
        };
        
        const response = await new APIClient(url).call('PUT', requestBody);
        
        if (response.status === 'OK') {
            // Invalidar el cache para este field para forzar recarga
            delete this.cache[field];
            // Si se actualizó una sensibilidad de usuario, necesitamos recargar desde el servidor
            // para obtener la sensibilidad correcta (usuario tiene prioridad sobre agencia)
            this.cacheLoaded = false;
            return response.data;
        }
        
        throw new Error(response.error_message || 'Error al actualizar sensibilidad');
    }

    /**
     * Borra la sensibilidad de usuario para un field específico
     * @param {string} field - Field ID
     * @returns {Promise<Object>} - Respuesta de la eliminación
     */
    static async deleteUserFieldSensitivity(field) {
        const url = `${API_URL}looker-cc/field-sensitivity/${field}/`;
        const response = await new APIClient(url).call('DELETE');
        
        if (response.status === 'OK') {
            // Invalidar el cache para este field para forzar recarga
            delete this.cache[field];
            this.cacheLoaded = false;
            return response.data;
        }
        
        throw new Error(response.error_message || 'Error al borrar sensibilidad de usuario');
    }

    /**
     * Limpia el cache de sensibilidades
     */
    static clearCache() {
        this.cache = {};
        this.cacheLoaded = false;
    }

    /**
     * Calcula el desvío en valor absoluto (porcentaje)
     * @param {number} currentValue - Valor actual
     * @param {number} comparisonValue - Valor de comparación
     * @returns {number} - Desvío en porcentaje (siempre positivo)
     */
    static calculateDeviation(currentValue, comparisonValue) {
        if (comparisonValue === 0 || comparisonValue === null || comparisonValue === undefined) {
            return 0;
        }
        const deviation = ((currentValue - comparisonValue) / comparisonValue) * 100;
        return Math.abs(deviation);
    }

    /**
     * Obtiene la clase de color según el desvío y el indicatorBehavior
     * @param {number} currentValue - Valor actual
     * @param {number} comparisonValue - Valor de comparación
     * @param {string} indicatorBehavior - 'MORE_IS_BETTER' o 'LESS_IS_BETTER'
     * @param {Object} sensitivity - Objeto con sensitivity_value_1 y sensitivity_value_2
     * @returns {string} - Clase de color: 'normal', 'good', 'bad', 'very-good', 'very-bad'
     */
    static getColorClass(currentValue, comparisonValue, indicatorBehavior, sensitivity) {
        if (!sensitivity || currentValue === null || currentValue === undefined || 
            comparisonValue === null || comparisonValue === undefined) {
            return 'normal';
        }

        const deviation = this.calculateDeviation(currentValue, comparisonValue);
        const { sensitivity_value_1, sensitivity_value_2 } = sensitivity;
        
        // Determinar si el valor subió o bajó
        const isIncrease = currentValue > comparisonValue;
        
        // Clasificar según umbrales
        let category;
        if (deviation < sensitivity_value_1) {
            category = 'NORMAL';
        } else if (deviation < sensitivity_value_2) {
            category = 'GOOD';
        } else {
            category = 'VERY_GOOD_OR_BAD';
        }
        
        // Determinar color según category, indicatorBehavior e isIncrease
        if (category === 'NORMAL') {
            return 'normal';
        }
        
        if (category === 'GOOD') {
            if (indicatorBehavior === 'MORE_IS_BETTER') {
                return isIncrease ? 'good' : 'bad';
            } else { // LESS_IS_BETTER
                return isIncrease ? 'bad' : 'good';
            }
        }
        
        // category === 'VERY_GOOD_OR_BAD'
        if (indicatorBehavior === 'MORE_IS_BETTER') {
            return isIncrease ? 'very-good' : 'very-bad';
        } else { // LESS_IS_BETTER
            return isIncrease ? 'very-bad' : 'very-good';
        }
    }
}

